import { ChangeEvent } from 'react';
import { getIn, useField, useFormikContext } from 'formik';
import { Alert, Button, FormHelperText } from '@mui/material';

import { SelectField } from 'components';
import { CsvLookupFormField } from 'types';

import { csvLookupPresetOptions } from './config';

interface FormCsvLookupFieldProps {
  name: string;
  value: CsvLookupFormField;
}

export const FormCsvLookupField = ({
  name,
  value,
}: FormCsvLookupFieldProps) => {
  const [, , { setValue }] = useField<CsvLookupFormField>(name);
  const { errors, touched, submitCount } = useFormikContext();
  const { datasetId, sourceFile } = value.options;
  const optionsPath = `${name}.options`;
  const optionsError = getIn(errors, optionsPath);
  const optionsTouched = getIn(touched, optionsPath);
  const csvSourceError =
    (submitCount > 0 || Boolean(optionsTouched)) &&
    typeof optionsError === 'string'
      ? optionsError
      : undefined;

  const selectCsv = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    event.target.value = '';

    setValue(
      {
        ...value,
        options: {
          ...value.options,
          sourceFile: file,
        },
      },
      true
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <SelectField
        fullWidth
        label="Preset"
        name={`${name}.options.preset`}
        options={csvLookupPresetOptions}
      />

      <Button component="label" variant="outlined">
        {sourceFile ? 'Replace CSV file' : 'Select CSV file'}
        <input hidden type="file" onChange={selectCsv} accept=".csv,text/csv" />
      </Button>

      {csvSourceError && (
        <FormHelperText error className="!ml-0">
          {csvSourceError}
        </FormHelperText>
      )}

      {sourceFile && (
        <Alert severity="info">
          {sourceFile.name} will be uploaded when the form is saved.
        </Alert>
      )}

      {!sourceFile && datasetId && (
        <Alert severity="info">
          The new form version will reuse the existing CSV data.
        </Alert>
      )}
    </div>
  );
};
